import { Icon } from "@/assets/icons";
import { Loading } from "@/components";
import { storeInspectionService } from "@/services/storeInspectionService";
import type { StoreInspectionDetail } from "@/services/storeInspectionService";
import { DEFAULT_ERROR_MESSAGE } from "@/types";
import { cn, createQueryString, toastError } from "@/utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const CARD_SHADOW =
  "0 0 2px 0 rgba(145, 158, 171, 0.20), 0 12px 24px -4px rgba(145, 158, 171, 0.12)";

const CONTENT_LABELS: Record<string, string> = {
  SERVICE_FEE_DISPLAY: "整備料金の表示",
  TIRE_DISPOSAL_FEE: "廃タイヤ処理料金",
  DUAL_PRICE_VIOLATION: "二重価格表示の表示基準違反",
  NEW_LABEL_DISPLAY: "New等の表示",
  LOW_FUEL_CONSUMPTION_DISPLAY: "低燃費等の表示",
  SPECIFIC_TERM_USAGE: "特定用語の使用",
  FALSE_DISCOUNT_TERMS: "事実に反する廉売用語",
  MISSING_LIMITATION_DETAILS: "限定内容の不表示",
  OTHERS: "その他",
  MEMBER_STICKER_DISPLAY: "会員証・ステッカーの掲示",
};

const Field = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <div>
    <p className="text-xs text-grey-600 mb-1">{label}</p>
    <p className="text-sm break-words">{value === 0 ? 0 : value || "-"}</p>
  </div>
);

const StoreInspectionResultDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);

  const [detail, setDetail] = useState<StoreInspectionDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const handleClose = () => {
    const query = createQueryString({
      page: params.page || "1",
      date_from: params.date_from,
      date_to: params.date_to,
    });
    navigate(`/store-inspection-results${query ? `?${query}` : ""}`);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    storeInspectionService
      .getStoreInspectionResultById(Number(id))
      .then(setDetail)
      .catch((error: any) => {
        toastError(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
        handleClose();
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="relative min-h-[calc(100vh-120px)]">
        <Loading />
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="relative">
      <div className="px-0 py-4 flex items-center gap-2 sticky top-0 bg-white z-10">
        <Icon
          name="close"
          onClick={handleClose}
          className="cursor-pointer hover:opacity-80 transition-opacity duration-300"
          color="#637381"
        />
        <p className="text-lg font-bold">店舗巡回結果詳細</p>
      </div>

      <div className="bg-grey-200 p-2 lg:p-4 lg:grid lg:grid-cols-3 flex flex-col gap-4 min-h-[calc(100vh-8rem)]">
        <div
          className="col-span-2 bg-white rounded-lg"
          style={{ boxShadow: CARD_SHADOW }}
        >
          <p className="text-base font-semibold px-4 py-[10px] border-b border-b-grey-300">
            違反内容追加
          </p>
          <div className="p-4">
            {!detail.violations.length && (
              <p className="text-sm text-grey-600">違反内容がありません。</p>
            )}
            {detail.violations.map((violation, index) => (
              <div
                key={violation.id}
                className={cn("space-y-4 mb-6", index > 0 && "mt-12")}
              >
                <p className="text-sm font-semibold">違反内容 {index + 1}</p>
                <Field
                  label="内容"
                  value={CONTENT_LABELS[violation.content] || violation.content}
                />
                <Field label="詳細" value={violation.eviolation_detail} />
                <Field label="コメント" value={violation.comment} />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="場所" value={violation.place} />
                  <Field label="詳細" value={violation.detail} />
                </div>
                {!!violation.attachments.length && (
                  <div>
                    <p className="text-xs text-grey-600 mb-2">添付ファイル</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {violation.attachments.map((file) => (
                        <a
                          key={file.id}
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg overflow-hidden border border-grey-300 hover:opacity-80"
                        >
                          <img
                            src={file.url}
                            alt={file.name}
                            className="w-full h-32 object-cover"
                          />
                          <p className="text-xs p-2 truncate">{file.name}</p>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div
          className="bg-white rounded-lg h-fit lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto"
          style={{ boxShadow: CARD_SHADOW }}
        >
          <p className="text-base font-semibold px-4 py-[10px] border-b border-b-grey-300">
            情報
          </p>
          <div className="p-4 space-y-4">
            <p className="text-sm font-semibold">調査情報</p>
            <div className="grid grid-cols-2 gap-2">
              <Field label="調査日" value={detail.survey_date} />
              <Field label="SV 名" value={detail.sv_name} />
            </div>
            <p className="text-sm font-semibold">基本情報</p>
            <div className="grid grid-cols-2 gap-2">
              <Field label="店頭名" value={detail.store_name} />
              <Field label="メール" value={detail.email} />
            </div>
            <Field label="住所" value={detail.address} />
            <p className="text-sm font-semibold">メール送信</p>
            <Field label="宛先セット名" value={detail.recipient_set_name} />
            <Field label="送信者" value={detail.sender_emails.join(", ")} />
            <Field label="CC" value={detail.cc_emails.join(", ")} />
            <div className="grid grid-cols-2 gap-2">
              <Field label="違反" value={detail.violation} />
              <Field label="口頭注意" value={detail.oral_warning} />
            </div>
            <div>
              <p className="text-sm font-semibold">その他</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <p className="text-sm">丁寧な説明</p>
                <p className="text-sm">{detail.thorough_explanation ? "あり" : "なし"}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <p className="text-sm">会員証ステッカー</p>
                <p className="text-sm">
                  {detail.business_certificate_sticker ? "あり" : "なし"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { StoreInspectionResultDetail };
